import type { LalamoveVehicleOption } from '../../lib/deliveryArrangements';

type VehicleIconKind =
  | 'motorcycle'
  | 'car'
  | 'mpv'
  | 'van'
  | 'lorry'
  | 'tow'
  | 'generic';

function iconKindForService(value: string): VehicleIconKind {
  const key = value.trim().toUpperCase();
  if (key === 'MOTORCYCLE' || key === 'COURIER') return 'motorcycle';
  if (key === 'CAR') return 'car';
  if (key === 'MPV') return 'mpv';
  if (key.includes('TOW')) return 'tow';
  if (key.includes('TRUCK') || key.includes('LORRY')) return 'lorry';
  if (key.includes('VAN') || key === 'MINIVAN') return 'van';
  return 'generic';
}

function VehicleGlyph({ kind }: { kind: VehicleIconKind }) {
  const common = {
    viewBox: '0 0 64 40',
    width: 56,
    height: 36,
    'aria-hidden': true as const,
  };

  if (kind === 'motorcycle') {
    return (
      <svg {...common}>
        <circle cx="14" cy="28" r="7" fill="none" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="48" cy="28" r="7" fill="none" stroke="currentColor" strokeWidth="2.5" />
        <path
          d="M20 28h10l6-12h8l4 8"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="36" y="8" width="14" height="10" rx="2" fill="currentColor" opacity="0.9" />
      </svg>
    );
  }

  if (kind === 'car') {
    return (
      <svg {...common}>
        <path
          d="M10 26h44l-3-8c-1-3-3-5-6-5H19c-3 0-5 2-6 5l-3 8z"
          fill="currentColor"
          opacity="0.92"
        />
        <path d="M18 13h10l3 5H16l2-5zm16 0h12l2 5H33l1-5z" fill="#fff" opacity="0.85" />
        <circle cx="18" cy="28" r="4.5" fill="#fff" stroke="currentColor" strokeWidth="2" />
        <circle cx="46" cy="28" r="4.5" fill="#fff" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }

  if (kind === 'mpv') {
    return (
      <svg {...common}>
        <path
          d="M8 27h48l-2-9c-1-4-4-7-8-7H18c-4 0-7 3-8 7l-2 9z"
          fill="currentColor"
          opacity="0.92"
        />
        <path d="M16 12h12l2 6H14l2-6zm16 0h14l2 6H31l1-6z" fill="#fff" opacity="0.85" />
        <circle cx="17" cy="29" r="4.2" fill="#fff" stroke="currentColor" strokeWidth="2" />
        <circle cx="47" cy="29" r="4.2" fill="#fff" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }

  if (kind === 'van') {
    return (
      <svg {...common}>
        <path
          d="M6 28h52V14c0-3-2-5-5-5H22c-2 0-4 1-5 3l-5 8H6v8z"
          fill="currentColor"
          opacity="0.92"
        />
        <path d="M24 11h26v8H21l3-8z" fill="#fff" opacity="0.82" />
        <circle cx="16" cy="30" r="4.2" fill="#fff" stroke="currentColor" strokeWidth="2" />
        <circle cx="48" cy="30" r="4.2" fill="#fff" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }

  if (kind === 'lorry') {
    return (
      <svg {...common}>
        <rect x="4" y="10" width="30" height="18" rx="1.5" fill="currentColor" opacity="0.92" />
        <path d="M34 16h12l8 8v4H34V16z" fill="currentColor" opacity="0.92" />
        <path d="M46 16v6h6l-6-6z" fill="#fff" opacity="0.75" />
        <circle cx="14" cy="30" r="4.2" fill="#fff" stroke="currentColor" strokeWidth="2" />
        <circle cx="44" cy="30" r="4.2" fill="#fff" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }

  if (kind === 'tow') {
    return (
      <svg {...common}>
        <path d="M8 26h20l4-10h10l6 10h6" fill="none" stroke="currentColor" strokeWidth="2.5" />
        <path d="M38 16h14v4H38z" fill="currentColor" />
        <circle cx="16" cy="28" r="4.2" fill="#fff" stroke="currentColor" strokeWidth="2" />
        <circle cx="46" cy="28" r="4.2" fill="#fff" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <rect x="10" y="12" width="44" height="16" rx="3" fill="currentColor" opacity="0.9" />
      <circle cx="18" cy="30" r="4" fill="#fff" stroke="currentColor" strokeWidth="2" />
      <circle cx="46" cy="30" r="4" fill="#fff" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

type LalamoveVehiclePickerProps = {
  options: LalamoveVehicleOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
};

export function LalamoveVehiclePicker({
  options,
  value,
  onChange,
  disabled,
  loading,
}: LalamoveVehiclePickerProps) {
  const selected = options.find((opt) => opt.value === value);

  return (
    <div className="lalamove-vehicle-picker">
      <div className="lalamove-vehicle-picker__head">
        <span className="field-label">Vehicle type *</span>
      </div>
      {loading ? (
        <p className="delivery-form-card__hint">Loading vehicle types…</p>
      ) : (
        <div
          className="lalamove-vehicle-picker__scroller"
          role="listbox"
          aria-label="Vehicle type"
          aria-disabled={disabled || undefined}
        >
          {options.map((opt) => {
            const active = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                className={[
                  'lalamove-vehicle-picker__card',
                  active ? 'lalamove-vehicle-picker__card--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={disabled}
                title={opt.detail ?? opt.description ?? opt.label}
                onClick={() => onChange(opt.value)}
              >
                <span className="lalamove-vehicle-picker__icon">
                  <VehicleGlyph kind={iconKindForService(opt.value)} />
                </span>
                <span className="lalamove-vehicle-picker__name">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
      {selected?.detail || selected?.description ? (
        <p className="delivery-form-card__hint lalamove-vehicle-picker__hint">
          {selected.detail ?? selected.description}
        </p>
      ) : null}
    </div>
  );
}
